import type {KpiOption} from '@hconnect/common/types'
import {
  appendUser,
  attachmentFilesToFormData,
  appendEquipmentData,
  appendDateData,
  appendStoppageSplitData
} from '@hconnect/common/utils'
import {Moment} from 'moment-timezone'

import type {Approver} from '../../approvers/types'
import type {User, EquipmentData} from '../../types'
import type {
  EventCreate,
  EventUpdate,
  TaskChecklistsOption,
  RunningTime,
  TaskRepetition,
  ParameterChangeData,
  Schedule
} from '../types'

import {isStoppage} from './eventType'

type Event = EventCreate | EventUpdate

// eslint-disable-next-line complexity
export const prepareEventFormData = async (
  event: Event,
  skipKeys: string[] = []
): Promise<FormData> => {
  const formData = new FormData()
  const keys = Object.keys(event).filter((item) => skipKeys.indexOf(item) < 0)
  for (const key of keys) {
    const value = event[key]
    if (value) {
      switch (key) {
        case 'attachments':
        case 'newAttachments':
          await attachmentFilesToFormData(value, formData)
          break
        case 'needsApproval':
          break
        case 'startDate':
        case 'endDate':
        case 'created':
        case 'lastModified':
        case 'lastModifiedStatus':
        case 'stoppageStart':
        case 'stoppageEnd':
        case 'dueDate':
        case 'doNotStartBefore':
          appendDateData(formData, value as Moment, key)
          break
        case 'attachmentsToRemove':
          value.map((url: string) => formData.append('attachmentsToRemove', url))
          break
        case 'repetitionInfo':
          appendRepetitionInfo(formData, value as TaskRepetition)
          break
        case 'equipment':
          appendEquipmentData(formData, value as EquipmentData)
          break
        case 'assignees':
          appendUsersData(formData, value as User[], 'assignees')
          break
        case 'createdBy':
          appendUser(formData, value as User, 'createdBy')
          break
        case 'parameterChange':
          appendParameterChangeData(formData, value as ParameterChangeData)
          break
        case 'approvers':
          appendApprovers(formData, value as Approver[])
          break
        case 'mainEquipment':
          appendEquipmentData(formData, value as EquipmentData, true)
          break
        case 'schedule':
          appendSchedule(formData, value as Schedule)
          break
        case 'pxTrendStoppage':
          appendPxTrendStoppage(formData, value as RunningTime)
          break
        case 'priority':
          if (value !== 0) {
            formData.append(key, value as string | Blob)
          }
          break
        case 'checklists':
          appendChecklistsData(
            formData,
            value as TaskChecklistsOption[],
            'linkedChecklistTemplateIds'
          )
          break
        case 'originalSplitStoppageId':
          if (isStoppage(event)) {
            const stoppageEvent = event as unknown as {stoppageStart: Moment}
            appendStoppageSplitData(
              formData,
              value as string,
              stoppageEvent.stoppageStart.toISOString()
            )
          }
          break
        default:
          formData.append(key, value as string | Blob)
      }
    }
  }
  return formData
}

export const appendRepetitionInfo = (formData: FormData, repetitionInfo: TaskRepetition) => {
  for (const key in repetitionInfo) {
    if (key === 'weekDays' && 'weekDays' in repetitionInfo) {
      repetitionInfo.weekDays.forEach((element) => {
        formData.append('repetitionInfo[weekDays]', element)
      })
    } else if (key === 'endDate' && 'startDate' in repetitionInfo && repetitionInfo.endDate) {
      appendDateData(formData, repetitionInfo.endDate, 'repetitionInfo[endDate]')
    } else if (key === 'startDate' && 'startDate' in repetitionInfo) {
      appendDateData(formData, repetitionInfo.startDate, 'repetitionInfo[startDate]')
    } else {
      formData.append(`repetitionInfo[${key}]`, repetitionInfo[key] as string | Blob)
    }
  }
}

export const appendSchedule = (formData: FormData, schedule: Schedule) => {
  for (const key in schedule) {
    const dateToAdd: Moment = schedule[key]
    appendDateData(formData, dateToAdd, `schedule[${key}]`)
  }
}

const dateKeys = ['begin', 'end', 'beginTechnical', 'endTechnical']
export const appendPxTrendStoppage = (formData: FormData, pxTrendStoppage: RunningTime) => {
  for (const key in pxTrendStoppage) {
    if (dateKeys.includes(key)) {
      if (pxTrendStoppage[key]) {
        appendDateData(formData, pxTrendStoppage[key] as Moment, `pxTrendStoppage[${key}]`)
      }
    } else {
      formData.append(`pxTrendStoppage[${key}]`, pxTrendStoppage[key] as string)
    }
  }
}

export const appendUsersData = (formData: FormData, users: User[], name: string) => {
  users.forEach((user, index) => {
    appendUser(formData, user, `${name}[${index}]`)
  })
}

export const appendParameterChangeData = (
  formData: FormData,
  parameterChangeData: ParameterChangeData
) => {
  for (const key in parameterChangeData) {
    if (parameterChangeData[key] || parameterChangeData[key] === false) {
      if (key === 'approvers') {
        appendUsersData(formData, parameterChangeData[key] as User[], `parameterChange[${key}]`)
      } else if (key === 'checkedBy') {
        appendUser(formData, parameterChangeData[key] as User, `parameterChange[${key}]`)
      } else {
        formData.append(`parameterChange[${key}]`, parameterChangeData[key] as string)
      }
    }
  }
}

export const appendApprovers = (formData: FormData, approvers: Approver[]) => {
  approvers.forEach((approver, index) => {
    formData.append(`approverUserIds[${index}]`, approver.user.id)
  })
}

export const appendChecklistsData = (
  formData: FormData,
  checklists: TaskChecklistsOption[],
  name: string
) => {
  checklists?.forEach((checklist, index) => {
    formData.append(`${name}[${index}]`, checklist.id)
  })
}
